import { UserEventModel } from '@/lib/models/user-event/user-event.model';
import { FC, useCallback } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { uniq } from 'lodash';
import { getDomainLabels, pickHighestRiskLevel } from './helpers';
import { EntityLink } from '@/components/entity-link';
import Chip from '@/components/chip';
import RiskChip from '@/components/chip/risk-chip';
import { Stack } from '@mui/material';
import { DateFormat, getNiceDate, getUserTimezone, pluralize } from '@/lib/helpers';

interface Props {
  events: UserEventModel[];
}

const ConversationItem: FC<Props> = ({ events }) => {
  const entityLinkId = events[0].conversationId || events[0].traceId;
  const { length: messageCount } = events;

  const violations = uniq(events.flatMap(({ violations }) => violations));
  const highestRiskLevel = pickHighestRiskLevel(events.map(({ riskLevel }) => riskLevel));
  const domainLabels = getDomainLabels(events);

  // Events in a conversation group are in ascending startTime order, so the first
  // and last bracket the conversation's time range.
  const { timezone } = getUserTimezone();
  const start = getNiceDate(events[0].startTime, DateFormat.TIME_12H, timezone).formatted;
  const end = getNiceDate(
    events[messageCount - 1].endTime,
    DateFormat.TIME_12H,
    timezone
  ).formatted;
  const title = start === end ? start : `${start} – ${end}`;

  const messages = `${messageCount} ${pluralize(messageCount, 'message')}`;
  const subtext = domainLabels ? `${messages} • ${domainLabels}` : messages;

  const navigate = useNavigate();
  const handleClick = useCallback(() => {
    // Open the User Activity page filtered to this conversation via the search
    // keyword.
    navigate({ to: '/user-events', search: { keyword: entityLinkId } });
  }, [entityLinkId, navigate]);

  const titleAdornment =
    violations.length || highestRiskLevel ? (
      <Stack direction="row" alignItems="center" gap={1}>
        {violations.map((violation) => (
          <Chip key={violation} size="x-small" color="error" label={violation.toUpperCase()} />
        ))}
        {highestRiskLevel && <RiskChip value={highestRiskLevel} size="x-small" />}
      </Stack>
    ) : undefined;

  return (
    <EntityLink
      title={title}
      titleAdornment={titleAdornment}
      subtext={subtext}
      onClick={handleClick}
    />
  );
};

export default ConversationItem;
